import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon, CircleMinusIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import { filter, isEmpty, negate, pipe, sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { generatePage } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { ScreenStepConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { isScreenStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { getHumanReadableStepName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateScreenPreview, {
  PREVIEW_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import {
  getConditionalPayload,
  getTransitionPayload,
} from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-run';
import DeviceIcon from 'dashboard/components/DeviceIcon';
import { VERIFICATION_TYPE_NAMES } from 'dashboard/lib/constants/verification';
import { formatTimeRangeDuration } from 'dashboard/lib/utils/formatters';
import { getTimeElapsed } from 'dashboard/lib/utils/getTimeElapsed';
import { AccountEvent } from 'dashboard/models/account';
import { Device } from 'dashboard/models/device';
import { Document, DocumentStatus } from 'dashboard/models/document';
import { Event } from 'dashboard/models/event';
import { Inquiry, InquiryEvent } from 'dashboard/models/inquiry';
import { DirectWorkflowRunEvent } from 'dashboard/models/inquiry-run';
import { InquirySession } from 'dashboard/models/inquiry-session';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  isCombinedStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunEvent, WorkflowRunType } from 'dashboard/models/workflow-run';
import { Label } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import ImageDot from 'images/dashboard/dot.svg';
import { EVENT_NAME_TO_DESCRIPTION, EventName } from 'lib/constants/events';
import {
  MINUTES_SECONDS_ELAPSED_FORMAT,
  SHORT_FORM_DATE,
  TIME_FORMAT,
} from 'lib/constants/luxonFormats';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { ApiModel } from 'lib/interfaces/api';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import { ThemeAttributes } from 'verify/models/theme';

const InquiryRunEventTimelinesContainer = styled.div`
  padding-bottom: ${Spacing[3]};
`;

const EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

/**
 * A map where the keys are IDs of transition events and the values are the previous transition event object.
 */
type TransitionEventsMap = { [id: string]: Event | undefined };

interface InquiryRunEventTimelinesProps {
  inquiry: Inquiry;
  events: Event[];
  users: User[];
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
}

const InquiryRunEventTimelines = ({
  inquiry,
  events: _events,
  users,
  workflows,
  workflowRuns,
  inquiryTemplateVersion,
  stepsPreview,
}: InquiryRunEventTimelinesProps) => {
  // Make sure events are ordered from latest to earliest.
  const events = useMemo(
    () => sortBy((event) => event.attributes['created-at'], _events).reverse(),
    [_events],
  );
  const transitionEventsMap = useMemo(
    () => getTransitionEventsMap(events.slice().reverse()),
    [events],
  );

  const daySegments = useMemo(() => getDaySegmentedInquiryRunEventTimelines()(events), [events]);

  if (events.length === 0) {
    return (
      <InquiryRunEventTimelineContainer>
        <EmptyWrapper>No events have occurred yet for this inquiry run</EmptyWrapper>
      </InquiryRunEventTimelineContainer>
    );
  }

  return (
    <InquiryRunEventTimelinesContainer>
      {daySegments.map((s, i) => (
        <InquiryRunEventTimelineByDay
          key={i}
          inquiry={inquiry}
          events={s}
          transitionEventsMap={transitionEventsMap}
          users={users}
          workflows={workflows}
          workflowRuns={workflowRuns}
          inquiryTemplateVersion={inquiryTemplateVersion}
          stepsPreview={stepsPreview}
        />
      ))}
    </InquiryRunEventTimelinesContainer>
  );
};

export default InquiryRunEventTimelines;

const splitEventsByDeviceChange = (
  [head, ...tail]: Event[],
  currSegment: Event[] = [],
  allSegments: Event[][] = [],
): Event[][] => {
  if (head == null) {
    return allSegments.concat([currSegment]);
  }
  return splitEventsByDeviceChange(tail, currSegment.concat(head), allSegments);
};

/**
 * Helper function to split a list of events by date so that we can display
 * when the day changes.
 */
const splitEventsByDay = (
  [head, ...tail]: Event[],
  currSegment: Event[] = [],
  allSegments: Event[][] = [],
): Event[][] => {
  if (head == null) {
    return allSegments.concat([currSegment]);
  }
  const lastEvent = currSegment[currSegment.length - 1];
  return lastEvent &&
    DateTime.fromISO(lastEvent.attributes['created-at']).startOf('day').toISO() !==
      DateTime.fromISO(head.attributes['created-at']).startOf('day').toISO()
    ? splitEventsByDay(tail, [head], allSegments.concat([currSegment]))
    : splitEventsByDay(tail, currSegment.concat(head), allSegments);
};

/**
 * Helper that does several things:
 * 1. subdivide them into a list of lists, split by the device change event
 * 2. filter out empty lists
 * 3. filter out lists that would be empty except for device
 */
export const getDeviceSegmentedInquiryRunEventTimelines = () =>
  pipe<[Event[]], Event[][], Event[][], Event[][]>(
    splitEventsByDeviceChange,
    filter<Event[]>(negate(isEmpty)),
    filter<Event[]>(
      (events) =>
        events[0].attributes.name !== EventName.InquirySessionStarted || events.length > 1,
    ),
  );

/**
 * Helper that does several things:
 * 1. subdivide them into a list of lists, split by days
 * 2. filter out empty lists
 */
export const getDaySegmentedInquiryRunEventTimelines = () =>
  pipe<[Event[]], Event[][], Event[][]>(splitEventsByDay, filter<Event[]>(negate(isEmpty)));

interface InquiryRunEventTimelineByDayProps {
  inquiry: Inquiry;
  events: Event[];
  transitionEventsMap: TransitionEventsMap;
  users: User[];
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
}

const InquiryRunEventTimelineByDay = ({
  inquiry,
  events,
  transitionEventsMap,
  users,
  workflows,
  workflowRuns,
  inquiryTemplateVersion,
  stepsPreview,
}: InquiryRunEventTimelineByDayProps) => {
  const date = events[0].attributes['created-at'];
  const segments = useMemo(() => getDeviceSegmentedInquiryRunEventTimelines()(events), [events]);
  return (
    <InquiryRunEventTimelineContainer>
      <MetadataContainer>
        <MetadataHeaderContainer>
          <MetadataHeader>{DateTime.fromISO(date).toFormat(SHORT_FORM_DATE)}</MetadataHeader>
        </MetadataHeaderContainer>
      </MetadataContainer>
      {segments.map((s, i) => (
        <InquiryRunEventTimeline
          key={i}
          inquiry={inquiry}
          events={s}
          transitionEventsMap={transitionEventsMap}
          users={users}
          workflows={workflows}
          workflowRuns={workflowRuns}
          inquiryTemplateVersion={inquiryTemplateVersion}
          stepsPreview={stepsPreview}
        />
      ))}
    </InquiryRunEventTimelineContainer>
  );
};

interface InquiryRunEventTimelineProps {
  events: Event[];
  users: User[];
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
}

const InquiryRunEventTimelineContainer = styled.div`
  margin-top: ${Spacing[2]};
`;

interface InquiryRunEventTimelineProps extends InquiryRunEventTimelinesProps {
  transitionEventsMap: TransitionEventsMap;
}

const InquiryRunEventTimeline = ({
  inquiry,
  events,
  transitionEventsMap,
  users,
  workflows,
  workflowRuns,
  inquiryTemplateVersion,
  stepsPreview,
}: InquiryRunEventTimelineProps) => (
  <InquiryRunEventTimelineContainer>
    <DeviceMetadata events={events} />
    {events.map((event, i) => (
      <EventItem
        key={event.id}
        inquiry={inquiry}
        event={event}
        events={events}
        users={users}
        workflows={workflows}
        workflowRuns={workflowRuns}
        inquiryTemplateVersion={inquiryTemplateVersion}
        stepsPreview={stepsPreview}
        transitionEventsMap={transitionEventsMap}
        isLast={i === events.length - 1}
      />
    ))}
  </InquiryRunEventTimelineContainer>
);

const MetadataContainer = styled.div`
  position: relative;
  margin: 0 ${Spacing[1]} ${Spacing[1]};

  &:after {
    border-top: 1px solid ${({ theme }) => theme.color.border.primary};
    content: '';
    position: absolute;
    top: ${Spacing[3]};
    width: 100%;
  }
`;

const MetadataBody = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-row-gap: ${Spacing[2]};
`;

const MetadataHeaderContainer = styled.div`
  display: flex;
`;

const MetadataHeader = styled('h4')`
  background: ${({ theme }) => theme.color.background.primary};
  display: flex;
  /* places item above divider line */
  z-index: 1;
  color: ${({ theme }) => theme.color.text.primary};
  padding-right: ${Spacing[3]};
`;

const MetadataIconContainer = styled.div`
  margin-right: ${Spacing[2]};
  display: flex;
  align-items: center;

  > svg {
    height: 16px;
    width: 16px;
  }
`;

const MetadataDesc = styled('span')`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  color: ${({ theme }) => theme.color.text.secondary};
`;

interface MetadataProps {
  events: Event[];
}

const DeviceMetadata = ({ events }: MetadataProps) => {
  if (events[0] == null || events[0].attributes.name !== EventName.InquirySessionStarted) {
    return null;
  }

  const timeElapsed = getTimeElapsed(
    events.map((e) => e.attributes['created-at']),
    MINUTES_SECONDS_ELAPSED_FORMAT,
  );

  const session = events[0].attributes.payload.data as InquirySession;
  const deviceId = session.relationships.device.data?.id;
  const device = deviceId
    ? (events[0].attributes.payload.included?.find((obj) => obj.id === deviceId) as Device)
    : null;

  const {
    'browser-full-version': browserFullVersion,
    'browser-name': browserName,
    'device-name': deviceName,
    'device-type': deviceType,
    'os-full-version': osFullVersion,
    'os-name': osName,
  } = device?.attributes || {};

  const os = [osName, osFullVersion].filter(Boolean).join(' ');
  const browser = [browserName, browserFullVersion].filter(Boolean).join(' ');
  const metadataDesc = [browser, os, deviceType].filter(Boolean).join(', ');

  return (
    <MetadataContainer>
      <MetadataBody>
        {device && (
          <MetadataHeaderContainer>
            <MetadataHeader>
              <MetadataIconContainer>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <DeviceIcon deviceType={deviceType} deviceName={deviceName} />
              </MetadataIconContainer>
              New Device (Duration: {timeElapsed})
            </MetadataHeader>
          </MetadataHeaderContainer>
        )}
        <MetadataDesc>{metadataDesc}</MetadataDesc>
      </MetadataBody>
    </MetadataContainer>
  );
};

const EventItemIconContainer = styled.div`
  margin: 0 ${Spacing[5]} 0 0;
  position: relative;
`;

const EVENT_ICON_WIDTH = 24;
const EventItemIcon = styled.div`
  width: ${EVENT_ICON_WIDTH}px;
  display: inline-block;
  outline: 2px solid ${({ theme }) => theme.color.background.primary};
  position: relative;
  border-radius: ${EVENT_ICON_WIDTH / 2}px;
  background-color: ${({ theme }) => theme.color.background.primary};
  color: ${({ theme }) => theme.color.border.secondary};
  z-index: 1;

  svg {
    display: block; // removes space for decenders that svg's default inline has
    margin: auto; // center svg as a block element in parent component
  }
`;

const EventContainer = styled.div`
  display: flex;
  justify-items: center;
  // align events with timeline date header
  padding: ${Spacing[3]} ${Spacing[2]} ${Spacing[3]} ${Spacing[1]};

  &:not(:last-child) {
    ${EventItemIconContainer}:after {
      content: '';
      position: absolute;
      top: ${Spacing[2]};
      height: 100%;
      border-left: 4px solid ${({ theme }) => theme.color.border.secondary};
      border-bottom-left-radius: ${BORDER_RADIUSES.base};
      border-bottom-right-radius: ${BORDER_RADIUSES.base};
      padding-bottom: ${Spacing[5]};
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const Timestamp = styled(Label).attrs(() => ({ size: 'sm', color: 'secondary' }))`
  flex-shrink: 0;
  line-height: inherit;
  width: 90px;
`;

const EventItemDesc = styled.div`
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  color: ${({ theme }) => theme.color.text.primary};
`;

interface EventItemProps {
  inquiry: Inquiry;
  event: Event;
  events: Event[];
  users: User[];
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
  transitionEventsMap: TransitionEventsMap;
  isLast: boolean;
}

const EventItem = ({
  inquiry,
  event,
  events,
  users,
  workflows,
  workflowRuns,
  inquiryTemplateVersion,
  stepsPreview,
  transitionEventsMap,
  isLast,
}: EventItemProps) => {
  const { payload, name } = event.attributes;
  const createdAt = DateTime.fromISO(event.attributes['created-at']);
  const createdAtFormatted = createdAt.toFormat(TIME_FORMAT);
  const isPersonerd = useIsPersonerd();

  switch (name) {
    case EventName.AccountStatusUpdated: {
      const accountData = payload.data as AccountEvent;
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            {statusDesc} to {accountData.attributes['account-status']}
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.DocumentCreated:
    case EventName.DocumentProcessed:
    case EventName.DocumentErrored: {
      const documentData = payload.data as Document;
      const documentStatus = documentData.attributes.status;
      const statusDesc = getDocumentStatusChangeName(documentStatus);
      if (statusDesc) {
        return (
          <EventContainer key={name} data-test='event-timeline__item'>
            <Timestamp>{createdAtFormatted}</Timestamp>
            <EventItemIconContainer>
              <EventItemIcon>
                <StatusChangeIcon status={documentStatus} isLast={isLast} />
              </EventItemIcon>
            </EventItemIconContainer>
            <EventItemDesc>{statusDesc}</EventItemDesc>
          </EventContainer>
        );
      } else {
        return null;
      }
    }

    case EventName.InquiryCreated:
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getInquiryEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryCompleted:
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleCheckIcon color={Color.BorderIconSuccess} height={24} width={24} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getInquiryEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryExpired:
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getInquiryEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.InquiryApproved:
    case EventName.InquiryDeclined:
    case EventName.InquiryMarkedForReview: {
      const inquiryData = event.attributes.payload.data as InquiryEvent;
      const description =
        inquiryData.attributes.reviewer != null
          ? actorDescription({
              preposition: 'by',
              actor: inquiryData.attributes.reviewer,
              actorType: inquiryData.attributes['reviewer-type'],
              comment: inquiryData.attributes['reviewer-comment'],
              workflows,
              workflowRuns,
              users,
              events,
            })
          : inquiryData.relationships.reviewer?.data != null
            ? actorDescription({
                preposition: 'by',
                actor: inquiryData.relationships.reviewer.data.id,
                actorType: inquiryData.relationships.reviewer.data.type,
                comment: inquiryData.attributes['reviewer-comment'],
                workflows,
                workflowRuns,
                users,
                events,
              })
            : null;
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getInquiryEventDescription(name)} {description}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.InquiryTransitioned: {
      const description = getTransitionEventDescription({
        inquiry,
        event,
        previousEvent: transitionEventsMap[event.id],
        inquiryTemplateVersion,
        stepsPreview,
      });

      // omit transition event if no next-step-name is found
      if (!description) return null;

      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>{description}</>
          </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.InquiryRunConditionalRouted: {
      const description = getConditionalDescriptionElement({
        event,
        inquiryTemplateVersion,
        isInternal: isPersonerd,
      });
      // omit transition event if no next-step-name is found
      return description ? (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>{description}</>
          </EventItemDesc>
        </EventContainer>
      ) : null;
    }

    case EventName.InquirySessionStarted:
    case EventName.InquirySessionCanceled:
    case EventName.InquirySessionExpired:
      return (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{getInquiryEventDescription(name)}</EventItemDesc>
        </EventContainer>
      );

    case EventName.VerificationCreated:
    case EventName.VerificationSubmitted:
    case EventName.VerificationPassed:
    case EventName.VerificationRequiresRetry:
    case EventName.VerificationFailed:
    case EventName.VerificationCanceled:
    case EventName.VerificationTentativelyPassed:
    case EventName.VerificationTentativelyFailed: {
      const verificationData = payload.data as Verification;
      const verificationStatus = verificationData.attributes.status;
      const verificationType = verificationData.type;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const statusDesc = getVerificationStatusChangeName(verificationType, verificationStatus);
      return statusDesc ? (
        <EventContainer key={name}>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <StatusChangeIcon status={verificationStatus} isLast={isLast} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{statusDesc}</EventItemDesc>
        </EventContainer>
      ) : null;
    }

    case EventName.WorkflowRunErrored: {
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{statusDesc} </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.WorkflowRunResumed: {
      const statusDesc = EVENT_NAME_TO_DESCRIPTION[name];
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>{statusDesc} </EventItemDesc>
        </EventContainer>
      );
    }

    case EventName.WorkflowRunCreated: {
      const workflowRunData = event.attributes.payload.data as WorkflowRunEvent;
      return (
        <EventContainer key={name} data-test='event-timeline__item'>
          <Timestamp>{createdAtFormatted}</Timestamp>
          <EventItemIconContainer>
            <EventItemIcon>
              <ImageDot height={12} width={12} />
            </EventItemIcon>
          </EventItemIconContainer>
          <EventItemDesc>
            <>
              {getWorkflowRunEventDescription(event, workflowRuns, workflows)}{' '}
              {actorDescription(
                'by',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                workflowRunData.relationships.creator?.data?.id,
                workflowRunData.relationships.creator?.data?.type,
                workflows,
                workflowRuns,
                users,
                events,
              )}
            </>
          </EventItemDesc>
        </EventContainer>
      );
    }

    // TODO: add values for these if we ever want to display in the timeline
    case EventName.AccountCreated:
    case EventName.AccountRedacted:
    case EventName.AccountArchived:
    case EventName.AccountRestored:
    case EventName.AccountConsolidated:
    case EventName.AccountTagAdded:
    case EventName.AccountTagRemoved:
    case EventName.AccountUpdatedFields:
    case EventName.CaseCreated:
    case EventName.CaseAssigned:
    case EventName.CaseQueueUpdated:
    case EventName.CaseReopened:
    case EventName.CaseResolved:
    case EventName.CaseSlaExpired:
    case EventName.CaseStatusUpdated:
    case EventName.CaseUpdated:
    case EventName.CaseMessageSent:
    case EventName.CaseMessageReceived:
    case EventName.ExternalIntegrationLogCreated: // todo: ariel will finish frontend component in the following pr
    case EventName.GraphQueryCancelled:
    case EventName.GraphQueryCompleted:
    case EventName.GraphQueryErrored:
    case EventName.GraphQueryReran:
    case EventName.GraphQuerySubmitted:
    case EventName.GraphQueryTimedOut:
    case EventName.InquiryFailed:
    case EventName.InquiryStarted:
    case EventName.InquiryRedacted:
    case EventName.InquiryReusablePersonaCreated:
    case EventName.InquiryReusablePersonaUsed:
    case EventName.InquiryTemplateVersionCreated:
    case EventName.InquiryTemplateVersionPublished:
    case EventName.DocumentSubmitted:
    case EventName.DocumentExtracted:
    case EventName.ReportAddressLookupErrored:
    case EventName.ReportAddressLookupReady:
    case EventName.ReportAdverseMediaDismissed:
    case EventName.ReportAdverseMediaErrored:
    case EventName.ReportAdverseMediaMatched:
    case EventName.ReportAdverseMediaReady:
    case EventName.ReportBusinessAdverseMediaDismissed:
    case EventName.ReportBusinessAdverseMediaErrored:
    case EventName.ReportBusinessAdverseMediaMatched:
    case EventName.ReportBusinessAdverseMediaReady:
    case EventName.ReportBusinessAssociatedPersonsErrored:
    case EventName.ReportBusinessAssociatedPersonsMatched:
    case EventName.ReportBusinessAssociatedPersonsReady:
    case EventName.ReportBusinessClassificationErrored:
    case EventName.ReportBusinessClassificationReady:
    case EventName.ReportBusinessIndustryClassificationErrored:
    case EventName.ReportBusinessIndustryClassificationReady:
    case EventName.ReportBusinessIndustryClassificationSubmitted:
    case EventName.ReportBusinessLiensLookupErrored:
    case EventName.ReportBusinessLiensLookupReady:
    case EventName.ReportBusinessLookupErrored:
    case EventName.ReportBusinessLookupReady:
    case EventName.ReportBusinessNonprofitChanged:
    case EventName.ReportBusinessNonprofitErrored:
    case EventName.ReportBusinessNonprofitReady:
    case EventName.ReportBusinessOnlinePresenceErrored:
    case EventName.ReportBusinessOnlinePresenceReady:
    case EventName.ReportBusinessOnlinePresenceSubmitted:
    case EventName.ReportBusinessRegistrationsLookupErrored:
    case EventName.ReportBusinessRegistrationsLookupReady:
    case EventName.ReportBusinessSocialMediaErrored:
    case EventName.ReportBusinessSocialMediaReady:
    case EventName.ReportBusinessWatchlistDismissed:
    case EventName.ReportBusinessWatchlistErrored:
    case EventName.ReportBusinessWatchlistMatched:
    case EventName.ReportBusinessWatchlistReady:
    case EventName.ReportBusinessWebsiteErrored:
    case EventName.ReportBusinessWebsiteReady:
    case EventName.ReportClearbitBusinessLookupErrored:
    case EventName.ReportClearbitBusinessLookupReady:
    case EventName.ReportCryptoAddressWatchlistErrored:
    case EventName.ReportCryptoAddressWatchlistMatched:
    case EventName.ReportCryptoAddressWatchlistReady:
    case EventName.ReportCustomListDismissed:
    case EventName.ReportCustomListErrored:
    case EventName.ReportCustomListMatched:
    case EventName.ReportCustomListReady:
    case EventName.ReportEmailAddressErrored:
    case EventName.ReportEmailAddressReady:
    case EventName.ReportFinraBrokerCheckErrored:
    case EventName.ReportFinraBrokerCheckReady:
    case EventName.ReportKyckrBusinessLookupReady:
    case EventName.ReportKyckrBusinessLookupErrored:
    case EventName.ReportMiddeskErrored:
    case EventName.ReportMiddeskReady:
    case EventName.ReportMxAccountErrored:
    case EventName.ReportMxAccountReady:
    case EventName.ReportPhoneNumberErrored:
    case EventName.ReportPhoneNumberReady:
    case EventName.ReportPoliticallyExposedPersonDismissed:
    case EventName.ReportPoliticallyExposedPersonErrored:
    case EventName.ReportPoliticallyExposedPersonMatched:
    case EventName.ReportPoliticallyExposedPersonReady:
    case EventName.ReportProfileErrored:
    case EventName.ReportProfileReady:
    case EventName.ReportProfileNonAuthoritativeErrored:
    case EventName.ReportProfileNonAuthoritativeReady:
    case EventName.ReportScreeningErrored:
    case EventName.ReportScreeningReady:
    case EventName.ReportSecActionLookupErrored:
    case EventName.ReportSecActionLookupReady:
    case EventName.ReportSentilinkApplicationRiskErrored:
    case EventName.ReportSentilinkApplicationRiskReady:
    case EventName.ReportSentilinkScoresErrored:
    case EventName.ReportSentilinkScoresReady:
    case EventName.ReportSocialMediaErrored:
    case EventName.ReportSocialMediaReady:
    case EventName.ReportSyntheticErrored:
    case EventName.ReportSyntheticReady:
    case EventName.ReportWatchlistDismissed:
    case EventName.ReportWatchlistErrored:
    case EventName.ReportWatchlistMatched:
    case EventName.ReportWatchlistReady:
    case EventName.TransactionCreated:
    case EventName.TransactionLabeled:
    case EventName.TransactionRedacted:
    case EventName.TransactionStatusUpdated:
    case EventName.SARCreated:
    case EventName.SARFiled:
    case EventName.SelfieCreated:
    case EventName.SelfieSubmitted:
    case EventName.SelfieProcessed:
    case EventName.SelfieErrored:
    case EventName.SessionCreated:
    case EventName.SessionExpired:
      return null;
    default:
      assertNever(name);
  }
};

const StatusChangeIcon = ({
  status,
  isLast,
}: {
  status: DocumentStatus | VerificationStatus;
  isLast: boolean;
}) => {
  switch (status) {
    case DocumentStatus.Initiated:
    case VerificationStatus.Initiated:
    case VerificationStatus.Confirmed:
      return <ImageDot height={12} width={12} />;
    case VerificationStatus.Submitted:
      return isLast ? (
        <CircleMinusIcon color={Color.BorderIconWarning} height={24} width={24} />
      ) : (
        <ImageDot height={12} width={12} />
      );
    case DocumentStatus.Processed:
    case VerificationStatus.Passed:
      return <CircleCheckIcon color={Color.BorderIconSuccess} height={24} width={24} />;
    case DocumentStatus.Errored:
    case VerificationStatus.RequiresRetry:
    case VerificationStatus.Failed:
    case VerificationStatus.Canceled:
      return <CircleXIcon color={Color.BorderIconDanger} height={24} width={24} />;
    case VerificationStatus.TentativelyPassed:
    case VerificationStatus.TentativelyFailed:
    case VerificationStatus.Skipped:
      return <CircleMinusIcon color={Color.BorderIconWarning} height={24} width={24} />;
    case DocumentStatus.Submitted:
      return null;
    default:
      assertNever(status);
  }
};

const getDocumentStatusChangeName = (status: DocumentStatus) => {
  switch (status) {
    case DocumentStatus.Initiated:
      return 'Document started';
    case DocumentStatus.Processed:
      return 'Document uploaded';
    case DocumentStatus.Errored:
      return 'Document errored';
    case DocumentStatus.Submitted:
      return null;
    default:
      assertNever(status);
  }
};

const getVerificationStatusChangeName = (type: ApiModel, status: VerificationStatus) => {
  const verificationName = VERIFICATION_TYPE_NAMES[type as Verification['type']];
  switch (status) {
    case VerificationStatus.Initiated:
      return `${verificationName} started`;
    case VerificationStatus.Confirmed:
      return `${verificationName} confirmed`;
    case VerificationStatus.Submitted:
      return `${verificationName} submitted`;
    case VerificationStatus.Passed:
      return `${verificationName} passed`;
    case VerificationStatus.TentativelyPassed:
      return `${verificationName} tentatively passed`;
    case VerificationStatus.TentativelyFailed:
      return `${verificationName} tentatively failed`;
    case VerificationStatus.RequiresRetry:
      return `${verificationName} requires retry`;
    case VerificationStatus.Failed:
      return `${verificationName} failed`;
    case VerificationStatus.Canceled:
      return `${verificationName} canceled`;
    case VerificationStatus.Skipped:
      return `${verificationName} skipped`;
    default:
      assertNever(status);
  }
};

const getInquiryEventDescription = (name: EventName) => {
  return EVENT_NAME_TO_DESCRIPTION[name];
};

const StepPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${FRAME_HEIGHT / 2}px;
  width: ${PREVIEW_WIDTH / 2}px;
  transform: scale(50%);
  & > * {
    cursor: auto;
  }
`;

const getStepDisplayText = (
  stepName: string,
  step: InquiryTemplateVersionStep | undefined,
): string => {
  return step?.['name-display'] ?? getHumanReadableStepName(stepName);
};

export const getTransitionEventDescription = ({
  inquiry,
  event,
  previousEvent,
  inquiryTemplateVersion,
  stepsPreview = [],
  withPrefix = true,
}: {
  inquiry: Inquiry;
  event: Event;
  previousEvent?: Event;
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[];
  withPrefix?: boolean;
}) => {
  const inquiryTemplateId = inquiryTemplateVersion?.relationships?.['inquiry-template']?.data?.id;
  const steps = inquiryTemplateVersion?.attributes?.steps ?? [];
  const theme = inquiryTemplateVersion?.attributes?.theme;
  const { prevStepName, nextStepName } = getTransitionPayload(
    event.attributes.payload.data as InquiryEvent,
  );

  if (!nextStepName) {
    return null;
  }

  const prevStep = steps.find(
    (step): step is ScreenStepConfig => isScreenStep(step) && step.name === prevStepName,
  );
  const nextStep = steps.find(
    (step): step is ScreenStepConfig => isScreenStep(step) && step.name === nextStepName,
  );

  const prefix = withPrefix ? 'Step changed ' : '';
  const transitionText = prevStepName
    ? `${prefix}from ${getStepDisplayText(prevStepName, prevStep)} to `
    : `${prefix}to `;
  const nextStepElement = buildLinkedStepElement({
    stepName: nextStepName,
    step: nextStep,
    stepsPreview,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    templateId: inquiryTemplateId,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    versionId: inquiryTemplateVersion?.id,
    theme,
  });
  const durationText = ` after ${formatTimeRangeDuration(previousEvent?.attributes['created-at'] ?? inquiry.attributes['created-at'], event.attributes['created-at']).completionTime}`;

  return (
    <>
      <span>{transitionText}</span>
      {nextStepElement}
      {durationText}
    </>
  );
};

const TimelineLink = styled(Link)`
  &:hover {
    text-decoration: underline;
  }
`;

type LinkedStepElementParams = {
  stepName: string;
  step: InquiryTemplateVersionStep | undefined;
  templateId: string;
  versionId: string;
  stepsPreview?: StepPreview[];
  theme?: ThemeAttributes;
  // deep link either to tree view or screen editor
  editorSubPath?: 'flow' | 'screens';
};

/**
 * Returns step display name element with 3 potential options:
 * 1. Link to ITE flow editor with hoverable preview if step exists in ITV and stepsPreview (client data)
 * 2. Link to ITE flow editor if step exists in ITV but stepsPreview is not provided
 * 3. Step display name as text if step is not found in ITV steps or stepsPreview
 */
export const buildLinkedStepElement = ({
  stepName,
  step,
  stepsPreview = [],
  templateId,
  versionId,
  theme,
  editorSubPath = 'flow',
}: LinkedStepElementParams) => {
  const stepDisplayText = getStepDisplayText(stepName, step);
  const stepTextElement = step ? (
    <TimelineLink
      target='_blank'
      rel='noreferrer'
      to={`/dashboard/inquiry-templates/${templateId}/${versionId}/${editorSubPath}/${stepName}`}
    >
      {stepDisplayText}
    </TimelineLink>
  ) : (
    <span>{stepDisplayText}</span>
  );
  const stepPreview = stepsPreview.find(({ name }) => stepName === name);
  const previewPages = stepPreview ? generatePage(stepPreview) : null;
  // find and show capture/record page as combined step preview
  let previewPage = previewPages?.[0];
  if (isCombinedStepConfig(step)) {
    previewPage =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      previewPages?.find((page) => ['capture', 'record'].includes(page.key.toString())) ??
      previewPages?.[0];
  }
  return stepPreview && previewPage ? (
    <Tooltip side='top' trigger={stepTextElement}>
      <StepPreviewWrapper>
        <InquiryTemplateScreenPreview theme={theme} page={previewPage} step={stepPreview} />
      </StepPreviewWrapper>
    </Tooltip>
  ) : (
    stepTextElement
  );
};

/**
 * Helper for generating an ordinal display string for conditional branches
 * encountered in an inquiry run which have null or empty string names. Assumes 1-based index.
 * @param branchIndex 1-based conditional branch index
 * @returns ordinal display string (integer + ordinal)
 */
const getBranchIndexOrdinalString = (branchIndex: number): string => {
  let ordinal = 'th';
  if (branchIndex % 10 === 1 && branchIndex % 100 !== 11) {
    ordinal = 'st';
  } else if (branchIndex % 10 === 2 && branchIndex % 100 !== 12) {
    ordinal = 'nd';
  } else if (branchIndex % 10 === 3 && branchIndex % 100 !== 13) {
    ordinal = 'rd';
  }
  return `${branchIndex}${ordinal}`;
};

export const getConditionalDescriptionElement = ({
  event,
  inquiryTemplateVersion,
  isInternal = false,
}: {
  event: Event;
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  isInternal?: boolean;
}): ReactNode => {
  const inquiryTemplateId = inquiryTemplateVersion?.relationships?.['inquiry-template']?.data?.id;
  const { conditionalName, treeNodeToken, workflowName, routeIndex, workflowRunId, workflowId } =
    getConditionalPayload(event.attributes.payload.data as DirectWorkflowRunEvent);
  const workflowNameText = workflowName ? `${workflowName} flow` : 'Inquiry flow';
  const conditionalNameText =
    conditionalName?.length > 0
      ? conditionalName.toLowerCase()
      : `${getBranchIndexOrdinalString(routeIndex)}`;
  const conditionalNameDisplay = `${conditionalNameText} branch`;
  const elementLink = isInternal
    ? `/dashboard/workflows/${workflowId}/runs/${workflowRunId}`
    : `/dashboard/inquiry-templates/${inquiryTemplateId}/${
        inquiryTemplateVersion?.id
      }/flow/${treeNodeToken}.${routeIndex - 1}`;
  const conditionalTextElement =
    treeNodeToken && inquiryTemplateId && inquiryTemplateVersion?.id ? (
      <TimelineLink target='_blank' rel='noreferrer' to={elementLink}>
        {conditionalNameDisplay}
      </TimelineLink>
    ) : (
      <span>{conditionalNameDisplay}</span>
    );
  return (
    <>
      <span>{`${workflowNameText} routed through the `}</span>
      {conditionalTextElement}
    </>
  );
};

/**
 * Consolidate workflow display naming for inquiry runs to return
 * [Workflow Name] workflow or 'Workflow' as a fallback
 */
const getWorkflowDisplayName = (workflow: Workflow | undefined): string => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return workflow?.attributes?.name?.length > 0
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      `${workflow.attributes.name} workflow`
    : 'Workflow';
};

interface ActorDescriptionArgs {
  preposition: string;
  actor: string;
  actorType: ApiModel.User | ApiModel.WorkflowRun | ApiModel.Workflow | ApiModel.Event;
  comment?: string | undefined;
  workflows: Workflow[];
  workflowRuns: WorkflowRun[];
  users: User[];
  events: Event[];
  defaultIfEmpty?: string;
}

const actorDescription = ({
  preposition,
  actor,
  actorType,
  comment,
  workflows,
  workflowRuns,
  users,
  events,
  defaultIfEmpty,
}: ActorDescriptionArgs) => {
  if (actor == null) {
    return `${defaultIfEmpty ?? ''}`;
  }

  const withComment = comment ? `with comment "${comment}"` : '';

  switch (actorType) {
    case ApiModel.User: {
      if (actor.startsWith('user_')) {
        const user = users.find((user) => user.id === actor);
        return user != null
          ? [preposition, user.attributes['email-address'], withComment].filter(Boolean).join(' ')
          : null;
      } else {
        return [preposition, actor, withComment].filter(Boolean).join(' ');
      }
    }

    case ApiModel.WorkflowRun: {
      const workflowRun = workflowRuns.find((run) => run.id === actor);
      if (workflowRun == null) {
        return null;
      }
      const workflowId = workflowRun.relationships.workflow.data.id;
      const actorWorkflow = workflows.find((w) => w.id === workflowId);
      let linkComponent;
      if (workflowRun.attributes['run-type'] === WorkflowRunType.Case) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const link = `/dashboard/case-actions/${actorWorkflow.id}/runs/${actor}`;
        linkComponent = (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <TimelineLink to={link}>Case Action: {actorWorkflow.attributes.name}</TimelineLink>
        );
      } else {
        const link = `/dashboard/workflows/${workflowId}/runs/${actor}`;
        linkComponent = (
          <TimelineLink to={link}>{getWorkflowDisplayName(actorWorkflow)}</TimelineLink>
        );
      }
      return (
        <>
          <span>{preposition} </span>
          {linkComponent}
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.Workflow: {
      const actorWorkflow = workflows.find((w) => w.id === actor);
      if (actorWorkflow == null) {
        return null;
      }
      const publishedVersionId = actorWorkflow.relationships['latest-published-version'].data.id;
      const workflowVersionLink = `/dashboard/workflows/${actorWorkflow.id}/${publishedVersionId}`;
      return (
        <>
          <span>{preposition} </span>
          <TimelineLink to={workflowVersionLink}>
            {getWorkflowDisplayName(actorWorkflow)}
          </TimelineLink>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }

    case ApiModel.Event: {
      const event = events.find((e) => e.id === actor);
      if (event == null) {
        return null;
      }
      const eventLink = `/dashboard/events/${event.id}`;
      return (
        <>
          <span>{preposition}</span>
          <TimelineLink to={eventLink}> {event.attributes.name} event</TimelineLink>
          {withComment && <span> {withComment}</span>}
        </>
      );
    }
  }
};

const getWorkflowRunEventDescription = (
  event: Event,
  workflowRuns: WorkflowRun[],
  workflows: Workflow[],
) => {
  const workflowRun = workflowRuns.find((run) => run.id === event.attributes.payload.data.id);
  const workflow = workflows.find(
    (wfl) => wfl.id === workflowRun?.relationships?.workflow?.data?.id,
  );
  const workflowName = getWorkflowDisplayName(workflow);
  const workflowNameComponent =
    workflow != null && workflowRun != null ? (
      <TimelineLink to={`/dashboard/workflows/${workflow.id}/runs/${workflowRun.id}`}>
        {workflowName}
      </TimelineLink>
    ) : (
      <span>{workflowName}</span>
    );
  const isManualRun = event.attributes?.context?.['is-manual-run'];
  const triggerCopy = isManualRun ? 'manually triggered' : 'triggered';
  return (
    <>
      {workflowNameComponent}
      <span> {triggerCopy}</span>
    </>
  );
};

/**
 * Takes a list of events ordered from earliest to latest
 */
export const getTransitionEventsMap = (events: Event[]): TransitionEventsMap =>
  events
    .filter((event) => event.attributes.name === EventName.InquiryTransitioned)
    .reduce<{ map: TransitionEventsMap; current: Event | undefined }>(
      (acc, event) => {
        acc.map[event.id] = acc.current;
        acc.current = event;
        return acc;
      },
      { map: {}, current: undefined },
    ).map;
